import type { FeatureCollection, Geometry, GeoJsonProperties } from 'geojson'
import type { MapBoxGL } from '../../MapBoxGL'

/**
 * Manages MapBox sources for station markers.
 *
 * Handles the creation and updating of three sources:
 * - unselected: markers that are not selected
 * - selected: markers that are selected
 * - all: all markers (for text labels)
 *
 * Each source is prefixed with a unique identifier to avoid conflicts.
 */
export class StationMapSourceManager {
  private readonly unselectedSourceId: string
  private readonly selectedSourceId: string
  private readonly allSourceId: string

  /**
   * Create a new source manager.
   *
   * @param prefix - Unique prefix for source names (e.g., 'stations', 'raingauges')
   */
  constructor(private readonly prefix: string) {
    this.unselectedSourceId = `unselected-${prefix}`
    this.selectedSourceId = `selected-${prefix}`
    this.allSourceId = `all-${prefix}`
  }

  /**
   * Get the source ID for unselected markers.
   */
  get unselectedSource(): string {
    return this.unselectedSourceId
  }

  /**
   * Get the source ID for selected markers.
   */
  get selectedSource(): string {
    return this.selectedSourceId
  }

  /**
   * Get the source ID for all markers (typically used for labels).
   */
  get allSource(): string {
    return this.allSourceId
  }

  /**
   * Get all source IDs managed by this instance.
   *
   * @returns Array of source IDs
   */
  getAllSourceIds(): string[] {
    return [this.unselectedSourceId, this.selectedSourceId, this.allSourceId]
  }

  /**
   * Update all sources with new feature collections.
   *
   * @param mapbox - MapBox GL instance
   * @param unselected - Features for unselected markers
   * @param selected - Features for selected markers
   * @param all - Features for all markers
   */
  updateSources(
    mapbox: MapBoxGL,
    unselected: FeatureCollection<Geometry, GeoJsonProperties>,
    selected: FeatureCollection<Geometry, GeoJsonProperties>,
    all: FeatureCollection<Geometry, GeoJsonProperties>,
  ): void {
    mapbox.updateSource(this.unselectedSourceId, unselected)
    mapbox.updateSource(this.selectedSourceId, selected)
    mapbox.updateSource(this.allSourceId, all)
  }
}
